import { isSignupPersonaSelectable, requiresPersonaApproval } from '../constants/personas';
import { setUserProfile } from '../services/userProfileService';
import { queueAccountApprovalEmail } from '../services/accountApprovalEmailService';
import { upsertPersonaApprovalRequest } from '../services/personaApprovalService';
import { LEGAL_DOC_VERSION } from '../legal/versions';

/**
 * @typedef {object} PersonaVerificationInput
 * @property {string} [companyName]
 * @property {string} [phone]
 * @property {string} [street]
 * @property {string} [city]
 * @property {string} [province]
 * @property {string} [postalCode]
 * @property {string} [country]
 */

/**
 * Fields persisted when the user accepts Terms + Privacy at signup.
 * @returns {{
 *   termsAcceptedAt: Date,
 *   privacyAcceptedAt: Date,
 *   termsVersion: string,
 *   privacyVersion: string,
 * }}
 */
export function buildLegalAcceptanceFields() {
  const now = new Date();
  return {
    termsAcceptedAt: now,
    privacyAcceptedAt: now,
    termsVersion: LEGAL_DOC_VERSION,
    privacyVersion: LEGAL_DOC_VERSION,
  };
}

/**
 * @param {string} persona
 * @param {PersonaVerificationInput} [verification]
 */
export function validatePersonaVerification(persona, verification = {}) {
  if (!requiresPersonaApproval(persona)) return null;
  const companyName = String(verification.companyName || '').trim();
  const phone = String(verification.phone || '').trim();
  const street = String(verification.street || '').trim();
  const city = String(verification.city || '').trim();
  const province = String(verification.province || '').trim();
  const postalCode = String(verification.postalCode || '').trim();
  if (!companyName) return 'signup.verification.companyRequired';
  if (!phone) return 'signup.verification.phoneRequired';
  if (!street || !city) return 'signup.verification.addressRequired';
  if (!province || !postalCode) return 'signup.verification.addressRequired';
  return null;
}

/**
 * @param {{
 *   uid: string,
 *   email?: string,
 *   persona: string,
 *   displayName?: string,
 *   locale?: 'fr' | 'en',
 *   verification?: PersonaVerificationInput,
 *   legalAccepted?: boolean,
 * }} params
 */
export async function registerPersonaProfile({
  uid,
  email = '',
  persona,
  displayName = '',
  locale = 'fr',
  verification = {},
  legalAccepted = false,
}) {
  const requested = persona || 'investisseur';
  if (!isSignupPersonaSelectable(requested)) {
    throw new Error('signup.accountType.notAvailable');
  }

  const navLang = typeof navigator !== 'undefined' ? navigator.language : 'fr';
  const language =
    locale === 'en' || locale === 'fr'
      ? locale
      : navLang.startsWith('fr')
        ? 'fr'
        : 'en';

  const legalFields = legalAccepted ? buildLegalAcceptanceFields() : {};

  const baseProfile = {
    persona: requested,
    onboardingCompleted: true,
    displayName: displayName || '',
    language,
    ...legalFields,
  };

  if (!requiresPersonaApproval(requested)) {
    await setUserProfile(uid, {
      ...baseProfile,
      personaApprovalStatus: 'approved',
    });
    return { approved: true };
  }

  const companyName = String(verification.companyName || '').trim();
  const phone = String(verification.phone || '').trim();
  const profilePayload = {
    ...baseProfile,
    requestedPersona: requested,
    personaApprovalStatus: 'pending',
    personaRequestedAt: new Date(),
    companyName,
    personalPhone: phone,
    personalAddressStreet: String(verification.street || '').trim(),
    personalAddressCity: String(verification.city || '').trim(),
    personalAddressProvince: String(verification.province || '').trim(),
    personalAddressPostalCode: String(verification.postalCode || '').trim(),
    personalAddressCountry: String(verification.country || '').trim() || 'Canada',
  };

  await setUserProfile(uid, profilePayload);
  await upsertPersonaApprovalRequest(uid, {
    email: String(email || '').trim().toLowerCase(),
    persona: requested,
    status: 'pending',
    companyName,
    phone,
    personalAddressStreet: profilePayload.personalAddressStreet,
    personalAddressCity: profilePayload.personalAddressCity,
    personalAddressProvince: profilePayload.personalAddressProvince,
    personalAddressPostalCode: profilePayload.personalAddressPostalCode,
    personalAddressCountry: profilePayload.personalAddressCountry,
    displayName: displayName || '',
  });
  await queueAccountApprovalEmail({
    uid,
    email,
    persona: requested,
    locale: language === 'en' ? 'en' : 'fr',
    displayName: displayName || '',
    companyName,
    phone,
    personalAddressStreet: profilePayload.personalAddressStreet,
    personalAddressCity: profilePayload.personalAddressCity,
    personalAddressProvince: profilePayload.personalAddressProvince,
    personalAddressPostalCode: profilePayload.personalAddressPostalCode,
    personalAddressCountry: profilePayload.personalAddressCountry,
  });

  return { approved: false, pending: true };
}
